import { Fragment } from 'react';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { Lock, LockOpen, LogOut, ShieldAlert, Loader2 } from 'lucide-react';
import { useVault } from '@/base/contexts/VaultContext';

function formatTtl(seconds: number): string {
  if (seconds <= 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

interface EmergencyLockButtonProps {
  collapsed?: boolean;
}

export function EmergencyLockButton({ collapsed = false }: EmergencyLockButtonProps) {
  const vault = useVault();

  // Loading state
  if (vault.isLoading) {
    return (
      <div className={`
        flex items-center gap-3 px-3 py-2 rounded-lg
        bg-[var(--grok-surface-2)] border border-[var(--grok-border)]
        ${collapsed ? 'justify-center' : ''}
      `}>
        <Loader2 className="w-5 h-5 animate-spin text-[var(--grok-text-secondary)]" />
        {!collapsed && (
          <span className="text-sm text-[var(--grok-text-secondary)]">Loading...</span>
        )}
      </div>
    );
  }

  // Not setup state
  if (!vault.isSetup) {
    return (
      <button
        onClick={vault.requestUnlock}
        title={collapsed ? 'Setup Vault' : undefined}
        className={`
          flex items-center gap-3 w-full px-3 py-2 rounded-lg
          bg-[var(--ems-warning-muted)] border border-[var(--ems-warning)]
          hover:bg-[var(--ems-warning)] hover:bg-opacity-20 transition-colors
          ${collapsed ? 'justify-center' : ''}
        `}
      >
        <ShieldAlert className="w-5 h-5 text-[var(--ems-warning)]" />
        {!collapsed && (
          <span className="text-sm font-medium text-[var(--ems-warning)]">Setup Vault</span>
        )}
      </button>
    );
  }

  // Locked state
  if (!vault.isUnlocked) {
    return (
      <button
        onClick={vault.requestUnlock}
        title={collapsed ? 'Vault Locked - Click to Unlock' : undefined}
        className={`
          flex items-center gap-3 w-full px-3 py-2 rounded-lg
          bg-[var(--ems-danger-muted)] border border-[var(--ems-danger)]
          hover:bg-[var(--ems-danger)] hover:bg-opacity-20 transition-colors
          ${collapsed ? 'justify-center' : ''}
        `}
      >
        <Lock className="w-5 h-5 text-[var(--ems-danger)]" />
        {!collapsed && (
          <span className="text-sm font-medium text-[var(--ems-danger)]">Vault Locked</span>
        )}
      </button>
    );
  }

  // Unlocked state with dropdown
  return (
    <Popover className="relative w-full">
      <PopoverButton
        title={collapsed ? `Vault Unlocked - ${vault.ttlRemaining !== null ? formatTtl(vault.ttlRemaining) : 'Active'}` : undefined}
        className={`
          flex items-center gap-3 w-full px-3 py-2 rounded-lg
          bg-[var(--grok-surface-2)] border border-[var(--grok-border)]
          hover:bg-[var(--grok-surface-3)] transition-colors
          focus:outline-none focus:ring-2 focus:ring-[var(--ems-accent)] focus:ring-offset-2 focus:ring-offset-[var(--grok-background)]
          ${collapsed ? 'justify-center' : ''}
        `}
      >
        <div className="relative flex-shrink-0">
          <LockOpen className="w-5 h-5 text-[var(--ems-success)]" />
          <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[var(--ems-success)] rounded-full animate-pulse" />
        </div>
        {!collapsed && (
          <div className="flex flex-col items-start flex-1 min-w-0">
            <span className="text-xs text-[var(--grok-text-secondary)]">CVS Vault</span>
            <span className="text-sm font-medium text-[var(--ems-success)]">
              {vault.ttlRemaining !== null ? formatTtl(vault.ttlRemaining) : 'Unlocked'}
            </span>
          </div>
        )}
      </PopoverButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <PopoverPanel
          className={`
            absolute z-50 w-64 origin-bottom
            ${collapsed ? 'left-full ml-2 bottom-0' : 'bottom-full mb-2 left-0'}
          `}
        >
          <div className="rounded-lg bg-[var(--grok-surface-2)] border border-[var(--grok-border)] shadow-lg overflow-hidden">
            <div className="p-3 border-b border-[var(--grok-border)]">
              <div className="text-sm font-medium text-[var(--grok-text-primary)]">
                CVS Vault
              </div>
              <div className="text-xs text-[var(--grok-text-secondary)] mt-1">
                Unlocked
                {vault.ttlRemaining !== null && (
                  <span className="ml-1 text-[var(--ems-success)]">
                    ({formatTtl(vault.ttlRemaining)} remaining)
                  </span>
                )}
              </div>
            </div>

            <div className="p-2 space-y-1">
              <button
                onClick={async () => {
                  await vault.lockVault();
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-[var(--grok-text-primary)] hover:bg-[var(--grok-surface-3)] transition-colors"
              >
                <Lock className="w-4 h-4" />
                <span>Lock Vault</span>
              </button>

              <button
                onClick={async () => {
                  await vault.lockAndSignOut();
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-[var(--ems-danger)] hover:bg-[var(--ems-danger-muted)] transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Lock & Sign Out</span>
              </button>
            </div>
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}
